/**
 * WiringTab — X-rays a single item's full pipeline: raw artifact data,
 * blueprint, card slots, actions, and UI type registry entry.
 *
 * Extracted from InspectorDrawer so the drawer shell stays thin.
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../lib/api';
import { safeStringify } from '@shared/safeStringify';
import { handlerDestination } from '@shared/view-system/handler-configs.schema';
import type { JsonValue } from '@shared/schemas/json-value';
import { fetchWiring } from '../../api/wiring';
import type { WiringData, WiringActionData } from '../../api/wiring';
import { RawJsonPanel } from './RawJsonPanel';
import { useCopiedFlash } from '../../hooks/useCopiedFlash';

import { WiringSection } from './WiringSection';
import { RegistryEntryCard } from './RegistryEntryCard';
import { debugTheme, truncateId } from './debugTheme';

const t = debugTheme.dark;

function truncateValue(value: unknown, max = 60): string {
  const s = typeof value === 'string' ? value : JSON.stringify(value);
  if (s == null) return '\u2014';
  return s.length > max ? s.slice(0, max) + '...' : s;
}

// Action card

function ActionCard({ wa }: { wa: WiringActionData }) {
  const { action } = wa;
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggle = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className={`${t.bgAlt} rounded ${t.border} border p-3 space-y-2`}>
      {/* Top line */}
      <div className="flex items-center justify-between font-mono text-[11px]">
        <span className={`${t.textBright} font-semibold`}>{action.id}</span>
      </div>

      {/* Metadata grid */}
      <div className={`grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] font-mono ${t.text}`}>
        <div>
          <span className={t.textMuted}>Type:</span>{' '}
          {wa.actionType?.id ?? '\u2014'}
        </div>
        <div>
          <span className={t.textMuted}>Interaction:</span> {action.interaction}
        </div>
        <div>
          <span className={t.textMuted}>Handler:</span> {action.handler}
        </div>
        <div>
          <span className={t.textMuted}>Outcome:</span> {action.outcome?.status ?? '\u2014'}
        </div>
        <div>
          <span className={t.textMuted}>isTerminal:</span>{' '}
          <span className={action.isTerminal ? t.boolTrue : t.boolFalse}>
            {action.isTerminal ? 'true' : 'false'}
          </span>
        </div>
      </div>

      {/* Config snippets — pretty-printed JSON */}
      {action.handlerConfig != null && (
        <div>
          <div className={`text-[11px] font-mono ${t.textMuted} mb-0.5`}>handlerConfig</div>
          {(() => {
            const cfg = action.handlerConfig as Record<string, unknown> | undefined;
            const callbackUrl = cfg && typeof cfg === 'object' ? handlerDestination('webhook', cfg as Record<string, JsonValue>) : undefined;
            return callbackUrl ? (
              <div className="text-[10px] font-mono bg-tangerine-500/15 text-tangerine-400 rounded px-2 py-1 mb-1 break-all">
                {'\u26A0\uFE0F Webhook: '}{callbackUrl}
              </div>
            ) : null;
          })()}
          <pre className={`text-[10px] font-mono ${t.text} bg-nightshade-900/50 rounded px-2 py-1.5 overflow-x-auto whitespace-pre-wrap break-all`}>
            {typeof action.handlerConfig === 'string' ? action.handlerConfig : JSON.stringify(action.handlerConfig, null, 2)}
          </pre>
        </div>
      )}
      {/* interactionConfig — use unknown access since TS narrows discriminated union too aggressively */}
      {(() => {
        const cfg = (action as Record<string, unknown>).interactionConfig;
        if (!cfg) return null;
        return (
          <details open className="group">
            <summary className={`text-[11px] font-mono ${t.textMuted} mb-0.5 cursor-pointer select-none list-none flex items-center gap-1`}>
              <span className={`${t.chevron} text-[9px] transition-transform group-open:rotate-90`}>{'\u25B6'}</span>
              interactionConfig
            </summary>
            <pre className={`text-[10px] font-mono ${t.text} bg-nightshade-900/50 rounded px-2 py-1.5 overflow-x-auto whitespace-pre-wrap break-all`}>
              {typeof cfg === 'string' ? cfg : JSON.stringify(cfg, null, 2)}
            </pre>
          </details>
        );
      })()}

      {/* Registry entry cards */}
      <div className="space-y-1 pt-1">
        {wa.actionType && (
          <RegistryEntryCard
            entry={wa.actionType}
            registryType="actionType"
            theme="dark"
            expanded={!!expanded['actionType']}
            onToggle={() => toggle('actionType')}
          />
        )}
        {wa.handler && (
          <RegistryEntryCard
            entry={wa.handler}
            registryType="handler"
            theme="dark"
            expanded={!!expanded['handler']}
            onToggle={() => toggle('handler')}
          />
        )}
        {wa.interaction && (
          <RegistryEntryCard
            entry={wa.interaction}
            registryType="interaction"
            theme="dark"
            expanded={!!expanded['interaction']}
            onToggle={() => toggle('interaction')}
          />
        )}
      </div>
    </div>
  );
}

// Main tab

export function WiringTab({ wiringTarget }: { wiringTarget: string | null }) {
  const { apiFetch } = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<WiringData | null>(null);

  useEffect(() => {
    setData(null);
    setError(null);

    if (!wiringTarget) return;

    let cancelled = false;
    setLoading(true);

    fetchWiring(apiFetch, wiringTarget)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [wiringTarget, apiFetch]);

  const [copied, flashCopied] = useCopiedFlash(2000);

  const copyWiring = useCallback(() => {
    if (!data) return;
    void navigator.clipboard.writeText(JSON.stringify(data, null, 2)).then(() => {
      flashCopied();
    });
  }, [data, flashCopied]);

  return (
    <>
      {/* Header content */}
      <div className={`flex-shrink-0 px-4 py-3 ${t.border} border-b ${t.bgAlt}`}>
        <div className="flex items-center gap-2 min-w-0 font-mono text-[11px]">
          {data && (
            <>
              <span className={t.textAccent2}>{data.artifact.kind}</span>
              <span className={t.textBright} title={data.artifact.id}>
                {truncateId(data.artifact.id, 16)}
              </span>
              <span className={t.textAccent}>{data.blueprint.artifactCard}</span>
              <span className="flex-1" />
              <button
                type="button"
                onClick={copyWiring}
                className={`text-[10px] font-mono px-1.5 py-0.5 rounded transition-colors ${
                  copied
                    ? 'bg-green-500/20 text-green-400'
                    : `${t.textMuted} hover:text-white bg-nightshade-700/50 hover:bg-nightshade-600/50`
                }`}
                title="Copy wiring JSON to clipboard"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </>
          )}
          {!data && wiringTarget && (
            <span className={t.textMuted}>{truncateId(wiringTarget, 24)}</span>
          )}
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex-1 flex items-center justify-center">
          <span className={`font-mono text-[11px] ${t.textMuted} animate-pulse`}>
            Loading wiring...
          </span>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="flex-1 flex items-center justify-center px-4">
          <span className={`font-mono text-[11px] ${t.statusMiss}`}>{error}</span>
        </div>
      )}

      {/* Content */}
      {data && !loading && (
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {/* Raw Data */}
          <WiringSection title="Raw Data" defaultOpen={false}>
            <RawJsonPanel data={data.artifact.data} />
          </WiringSection>

          {/* Blueprint */}
          <WiringSection title="Blueprint" defaultOpen={true}>
            <div className="space-y-3">
              {/* Blueprint metadata card */}
              <div className={`${t.bgAlt} rounded ${t.border} border p-3 space-y-2`}>
                <div className="flex items-center justify-between font-mono text-[11px]">
                  <span className={`${t.textBright} font-semibold`}>{data.blueprint.displayName}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    data.blueprint.status === 'published'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-amber-500/20 text-amber-400'
                  }`}>
                    {data.blueprint.status}
                  </span>
                </div>
                <div className={`grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] font-mono ${t.text}`}>
                  <div>
                    <span className={t.textMuted}>kind:</span>{' '}
                    {data.blueprint.kind}
                  </div>
                  <div>
                    <span className={t.textMuted}>intent:</span>{' '}
                    {data.blueprint.intent}
                  </div>
                  <div>
                    <span className={t.textMuted}>artifactCard:</span>{' '}
                    <span className={t.textAccent}>{data.blueprint.artifactCard}</span>
                  </div>
                  <div>
                    <span className={t.textMuted}>defaultView:</span>{' '}
                    {String(data.blueprint.defaultView ?? '\u2014')}
                  </div>
                </div>
              </div>

              {/* Card Slots */}
              {data.wiring.cardSlots.length > 0 && (
                <div className={`${t.bgAlt} rounded ${t.border} border p-3`}>
                  <div className={`text-[11px] font-mono font-semibold ${t.textBright} mb-2`}>
                    Card Slots
                  </div>
                  <div className="space-y-1">
                    {data.wiring.cardSlots.map((slot) => (
                      <div key={slot.slot} className={`flex items-center gap-2 text-[11px] font-mono ${t.text}`}>
                        <span className={slot.status === 'hit' ? t.statusHit : t.statusMiss}>
                          {'\u25CF'}
                        </span>
                        <span className={`${t.textBright} w-20 flex-shrink-0`}>{slot.slot}</span>
                        <span className={`${t.textMuted} w-28 flex-shrink-0 truncate`} title={slot.dataKey}>{slot.dataKey}</span>
                        <span className="truncate" title={safeStringify(slot.resolvedValue)}>
                          {truncateValue(slot.resolvedValue, 40)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detail Sections */}
              {data.wiring.detailSections.length > 0 && (
                <div className={`${t.bgAlt} rounded ${t.border} border p-3`}>
                  <div className={`text-[11px] font-mono font-semibold ${t.textBright} mb-2`}>
                    Detail Sections
                  </div>
                  <div className="space-y-1">
                    {data.wiring.detailSections.map((sec) => (
                      <div key={sec.key} className={`flex items-start gap-2 text-[11px] font-mono ${t.text}`}>
                        <span className={`${t.textBright} w-28 flex-shrink-0 truncate`} title={sec.key}>{sec.key}</span>
                        <span className={`${t.textMuted} w-20 flex-shrink-0`}>{sec.label}</span>
                        <span className="truncate" title={safeStringify(sec.resolvedValue)}>
                          {truncateValue(sec.resolvedValue, 40)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </WiringSection>

          {/* Actions */}
          <WiringSection
            title="Actions"
            count={data.wiring.actions.length}
            defaultOpen={true}
          >
            <div className="space-y-2">
              {data.wiring.actions.map((wa) => (
                <ActionCard key={wa.action.id} wa={wa} />
              ))}
              {data.wiring.actions.length === 0 && (
                <div className={`text-[11px] font-mono ${t.textMuted}`}>No actions</div>
              )}
            </div>
          </WiringSection>

          {/* UI Type */}
          <WiringSection title="UI Type" defaultOpen={false}>
            {data.wiring.artifactCard ? (
              <RegistryEntryCard
                entry={data.wiring.artifactCard}
                registryType="artifactCard"
                theme="dark"
                expanded={true}
              />
            ) : (
              <div className={`text-[11px] font-mono ${t.textMuted}`}>
                {data.blueprint.artifactCard} is not in the artifact card registry
              </div>
            )}
          </WiringSection>
        </div>
      )}
    </>
  );
}
